import { useState, useEffect, useRef } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  priority?: boolean;
  onLoad?: () => void;
}

export const LazyImage = ({
  src,
  alt,
  className = '',
  width,
  height,
  objectFit = 'cover',
  priority = false,
  onLoad,
}: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current) {
      // 檢查圖片是否已經在快取中
      if (imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
        setIsLoaded(true);
        setHasError(false);
        onLoad?.();
      }
    }
  }, [src, onLoad]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(false);
  };

  // 🔥 修復：根據 objectFit 決定 object-position
  const objectFitClass = 
    objectFit === 'cover' ? 'object-cover object-center' :
    objectFit === 'contain' ? 'object-contain object-center' :
    objectFit === 'fill' ? 'object-fill' :
    objectFit === 'scale-down' ? 'object-scale-down object-center' :
    'object-none object-center';

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* 骨架屏 */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/20 to-gray-800/20 animate-pulse rounded-lg" />
      )}

      {/* 錯誤提示 */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 rounded-lg">
          <div className="text-center text-gray-400">
            <i className="ri-image-line text-4xl mb-2"></i>
            <p className="text-xs">圖片載入失敗</p>
          </div>
        </div>
      )}

      {/* 🔥 修復：簡化圖片結構，移除不必要的絕對定位 */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={`w-full h-full ${objectFitClass} ${
          hasError ? 'hidden' : 'block'
        } ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-300 rounded-lg`}
        loading={priority ? 'eager' : 'lazy'}
        onLoad={handleLoad}
        onError={handleError}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    </div>
  );
};
